import { CartItemKeys } from '@config/keys/cart.keys';
import { ICartItem } from '@core/models/cart.model';
import { DoodMarketplaceCartScope, DoodMarketplaceModel } from '@core/models/marketplace.model';
import { DoodProductModel, IOverridesPrices } from '@core/models/product.model';
import { DoodShopModel } from '@core/models/shop.model';

export class CartUtils {
  /**
   * Total units in the cart: sum of each line's quantity.
   * This is not the number of distinct product lines (use `products.length` for that).
   */
  static sumActiveCartLineQuantities(
    products: ReadonlyArray<{ [CartItemKeys.Quantity]?: number | null }>,
  ): number {
    return products.reduce((sum, item) => sum + (item[CartItemKeys.Quantity] ?? 0), 0);
  }

  static generateId(marketplace: DoodMarketplaceModel, shop?: DoodShopModel | null): string {
    return marketplace.cart_scope === DoodMarketplaceCartScope.marketplace
      ? `marketplace_${marketplace.id}`
      : `shop_${shop?.id}`;
  }

  /**
   * Cart option / composition label. Quantity is shown only when greater than 1
   * (e.g. "ketchup x3").
   */
  static formatCartOptionLabel({
    name,
    quantity,
  }: {
    name?: string | null;
    quantity?: number | null;
  }): string {
    const label = name ?? '';
    const qty = quantity ?? 1;
    return qty > 1 ? `${label} x${qty}` : label;
  }

  /**
   * Computes the total line price in cents for one unit.
   * Same formula as CartService.getCartLineItems$: finalPrice + sum(child.final_price * child.quantity).
   * For declinables, uses price_overrides.additional_price for sub-products (same as mapProductCategories).
   */
  static getLineTotalPriceCents({
    product,
    cartItem,
    products,
  }: {
    product: DoodProductModel;
    cartItem: ICartItem;
    products: DoodProductModel[];
  }): number {
    const additions = cartItem[CartItemKeys.Additions];
    const declinableProducts = cartItem[CartItemKeys.Products];

    if (declinableProducts?.length) {
      const base = product?.final_price ?? 0;
      const priceOverridesByCategoryAndProduct = this.buildPriceOverridesLookup(product);
      const childrenTotal = declinableProducts.reduce((acc, subItem) => {
        const categoryId = subItem[CartItemKeys.StoreId] ?? '';
        const subProductId = subItem[CartItemKeys.Id];
        const qty = subItem[CartItemKeys.Quantity] ?? 1;
        const subPrice =
          priceOverridesByCategoryAndProduct[categoryId]?.[subProductId]?.additional_price ?? 0;
        const subAdditions = (subItem[CartItemKeys.Additions] ?? []).reduce(
          (addAcc, addId) => addAcc + (products.find(p => p.id === addId)?.final_price ?? 0),
          0,
        );
        return acc + subPrice * qty + subAdditions;
      }, 0);
      return base + childrenTotal;
    }

    const base = product?.final_price ?? 0;
    const additionsTotal = (additions ?? []).reduce(
      (acc, addId) => acc + (products.find(p => p.id === addId)?.final_price ?? 0),
      0,
    );
    return base + additionsTotal;
  }

  private static buildPriceOverridesLookup(
    product: DoodProductModel,
  ): Record<string, Record<string, IOverridesPrices>> {
    return (product.price_overrides ?? []).reduce(
      (acc, override) => {
        const categoryId = override.store;
        if (!categoryId) {
          return acc;
        }
        if (!acc[categoryId]) {
          acc[categoryId] = {};
        }
        acc[categoryId][override.product] = override;
        return acc;
      },
      {} as Record<string, Record<string, IOverridesPrices>>,
    );
  }
}
